import { Dispatch, SetStateAction, useCallback } from 'react';
import { SuggestionContactType } from '../../queries';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { SUGGESTION_CONTACT_FORM_FIELDS } from '../../constants';
import { FieldWrapper } from '@buildwise/libs/people';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import SuggestionAssignedToField from 'components/modals/addPostModal/SuggestionAssignedToField';
import { SuggestionContactAPI } from '../../apis';
import useUserData from 'hooks/useUserData';
import { toast } from 'react-toastify';

type SuggestionContactFormData = {
  groupName: string;
  region: string;
  taskOwner: string;
};

const suggestionContactSchema = Yup.object().shape({
  groupName: Yup.string().required('This is required'),
  region: Yup.string().required('This is required'),
  taskOwner: Yup.string().required('This is required'),
});
type AddSuggestionContactModalProps = {
  open: boolean;
  suggestionContact?: SuggestionContactType;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function AddSuggestionContactModal({
  open,
  suggestionContact,
  setOpen,
}: AddSuggestionContactModalProps) {
  const { companyId } = useUserData();
  const suggestionContactForm = useForm<SuggestionContactFormData>({
    resolver: yupResolver(suggestionContactSchema),
    defaultValues: {
      groupName: suggestionContact?.groupName || '',
      region: suggestionContact?.region || '',
      taskOwner: suggestionContact?.owner || '',
    },
  });
  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = suggestionContactForm;
  const groupName = useWatch({
    control,
    name: 'groupName',
  });
  const region = useWatch({
    control,
    name: 'region',
  });

  const closeModal = useCallback(() => {
    reset();
    setOpen(false);
  }, []);

  const onSubmit = useCallback(
    handleSubmit(async (values: SuggestionContactFormData) => {
      try {
        if (suggestionContact) {
          await SuggestionContactAPI.saveSuggestionContact({
            ...suggestionContact,
            groupName: values.groupName,
            region: values.region,
            owner: values.taskOwner,
          });
        } else {
          await SuggestionContactAPI.addSuggestionContact({
            companyId,
            groupName: values.groupName,
            region: values.region,
            owner: values.taskOwner,
          });
        }
        toast.success('Contact added successfully!');
        closeModal();
      } catch (error) {
        console.log('SUGGESTION_FORM_ERROR: ' + error);
      }
    }),
    [],
  );

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'black' }}>
          {suggestionContact ? 'Edit Contact' : 'Add Contact'}
        </Typography>
      </CustomDialogTitle>
      <DialogContent dividers id="dialog-description">
        <FormProvider {...suggestionContactForm}>
          {SUGGESTION_CONTACT_FORM_FIELDS.map((field, index) => (
            <FieldWrapper
              id={field.entryCode}
              label={field.referenceObject?.label || field.entryDescription}
              required={field.referenceObject?.isRequired}
              disabled={isSubmitting}
            >
              {field.entryCode === 'taskOwner' ? (
                <SuggestionAssignedToField
                  autoFocus={index === 0}
                  departments={groupName ? [groupName] : []}
                  regions={region ? [region] : []}
                  field={field}
                />
              ) : (
                <RenderFilterFieldByType autoFocus={index === 0} field={field} />
              )}
            </FieldWrapper>
          ))}
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={onSubmit} variant="contained">
          {isSubmitting ? (
            <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
          ) : suggestionContact ? (
            'Save'
          ) : (
            'Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSuggestionContactModal;
